import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { getCompanyTrades } from "../app/apis/subRatingsAPI";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import { ITrade, ITradeResponse } from "../app/interfaces/ITrade";
import { RootState } from "../app/store";

export interface TradesState {
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    data: ITrade[];
    error?: IErrorState;
}

const initialState: TradesState = {
    status: 'idle',
    data: [],
    error: undefined
};

export const getCompanyTradesThunk = createAsyncThunk(
    'trades/getCompanyTrades',
    async (companyId: number, { rejectWithValue }) => {
        try {
            return await getCompanyTrades(companyId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const tradesSlice = createSlice({
    name: 'trades',
    initialState,
    reducers: {
        clearTradesError: (state) => {
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCompanyTradesThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getCompanyTradesThunk.rejected, (state, action) => {
                state.status = 'failed';
                const errorResponse = action?.payload as IErrorState;
                state.error = errorResponse;
            })
            .addCase(getCompanyTradesThunk.fulfilled, (state, action) => {
                const response = action.payload as ITradeResponse;
                state.data = response.trades;
                state.status = 'succeeded';
            });
    }
});

export const { clearTradesError } = tradesSlice.actions;

export const selectTrades = (state: RootState) => state.trades.data;
export const selectTradesStatus = (state: RootState) => state.trades.status;
export const selectTradesError = (state: RootState) => state.trades.error;

export default tradesSlice.reducer;
